import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Badge,
  Card,
  Tabs,
  Table,
  LoadingState,
  ErrorState,
  OfflineState,
  Modal,
  useToast,
  type Column,
  type TabItem,
} from "@/ui";
import { useT, useUiStore } from "@/app";
import { useCan } from "@/auth";
import { token } from "@/design/theme";
import { PageHeader } from "../components/PageHeader";
import { BillingPanel } from "../components/BillingPanel";
import { TubesPanel } from "../components/TubesPanel";
import { Mono } from "../components/Mono";
import { useCancelDemande, useDemande } from "../lib/api";
import { CAPS } from "../lib/caps";
import { formatMontant } from "../lib/format";
import type { LigneDemande } from "../lib/types";

const STATUT_TONE: Record<string, "neutral" | "info" | "success" | "warn" | "danger"> = {
  brouillon: "neutral",
  ouverte: "info",
  tubes_generes: "success",
  annulee: "danger",
};

/**
 * DemandeWorkspacePage — the per-demande hub once it exists: line summary, then the billing handoff
 * and the tube/prélèvement steps as tabs. Drives a single useDemande query and renders all 5 states
 * (offline / loading / error / empty-lines / forbidden-by-route). Cancel is a guarded supersede (L-1).
 */
export function DemandeWorkspacePage() {
  const { t } = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const online = useUiStore((s) => s.online);
  const { id = "" } = useParams();

  const canCancel = useCan(CAPS.demandeDelete);
  const [tab, setTab] = useState("basket");
  const [confirmCancel, setConfirmCancel] = useState(false);

  const query = useDemande(id);
  const cancel = useCancelDemande(id);

  if (!online && !query.data) return <OfflineState onRetry={() => void query.refetch()} />;
  if (query.isLoading) return <LoadingState variant="table" />;
  if (query.isError) {
    return (
      <ErrorState
        message={query.error?.message ?? t("reception.error.generic")}
        code={query.error?.code}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const demande = query.data!;
  const lignes = demande.lignes.filter((l) => l.statut !== "annulee");
  const total =
    demande.total_ttc ?? lignes.reduce((s, l) => s + l.prix_applique - (l.remise_appliquee ?? 0), 0);

  const doCancel = () => {
    cancel.mutate(undefined, {
      onSuccess: () => {
        toast.success(t("reception.toast.demandeCancelled"));
        setConfirmCancel(false);
      },
      onError: (e) => toast.error(e.message || t("reception.error.generic")),
    });
  };

  const ligneColumns: ReadonlyArray<Column<LigneDemande>> = [
    {
      key: "code",
      header: t("reception.demande.col.code"),
      cell: (l) => <Mono>{l.parametre_code}</Mono>,
      mono: true,
    },
    {
      key: "libelle",
      header: t("reception.demande.col.libelle"),
      cell: (l) => l.libelle_snapshot,
    },
    {
      key: "prix",
      header: t("reception.demande.col.prix"),
      align: "end",
      mono: true,
      cell: (l) => <Mono>{formatMontant(l.prix_applique)}</Mono>,
    },
  ];

  const basketTab = (
    <Card padded={false}>
      <Table<LigneDemande>
        columns={ligneColumns}
        rows={lignes}
        rowKey={(l) => String(l.id)}
        caption={t("reception.demande.title")}
        emptyTitle={t("reception.demande.basket.empty")}
        emptyDescription={t("reception.demande.basket.emptyDesc")}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: token.s3,
          padding: token.s4,
          borderBlockStart: `1px solid ${token.line}`,
        }}
      >
        <span style={{ color: token.slate }}>{t("reception.demande.total")}</span>
        <strong>
          <Mono>{formatMontant(total)}</Mono>
        </strong>
      </div>
    </Card>
  );

  const tabs: TabItem[] = [
    { id: "basket", label: t("reception.demande.tab.basket"), content: basketTab },
    {
      id: "billing",
      label: t("reception.demande.tab.billing"),
      content: <BillingPanel demande={demande} />,
    },
    {
      id: "tubes",
      label: t("reception.demande.tab.tubes"),
      content: <TubesPanel demande={demande} />,
    },
  ];

  const tone = STATUT_TONE[demande.statut] ?? "neutral";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: token.s5 }}>
      <PageHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: token.s3 }}>
            {t("reception.demande.workspace.title", { ref: demande.reference })}
            <Badge tone={tone}>{t("reception.demande.statut")}: {demande.statut}</Badge>
          </span>
        }
        actions={
          <div style={{ display: "flex", gap: token.s2 }}>
            <Button variant="ghost" onClick={() => navigate("/reception")}>
              {t("reception.back")}
            </Button>
            {canCancel && demande.statut !== "annulee" && (
              <Button variant="danger" onClick={() => setConfirmCancel(true)}>
                {t("reception.demande.cancel")}
              </Button>
            )}
          </div>
        }
      />

      {demande.statut === "annulee" ? (
        <Card>
          <Badge tone="danger">{t("reception.toast.demandeCancelled")}</Badge>
          <div style={{ marginBlockStart: token.s4 }}>{basketTab}</div>
        </Card>
      ) : (
        <Tabs items={tabs} value={tab} onChange={setTab} aria-label={t("reception.demande.title")} />
      )}

      <Modal
        open={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        title={t("reception.demande.cancel")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmCancel(false)}>
              {t("reception.form.cancel")}
            </Button>
            <Button variant="danger" onClick={doCancel} loading={cancel.isPending}>
              {t("reception.demande.cancel")}
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>{t("reception.demande.cancelConfirm")}</p>
      </Modal>
    </div>
  );
}
